import { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, 
  Plus,
  Pencil,
  Trash2,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  Clock,
  Info,
  Shield,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import InfoHint from '../ui/info-hint';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import apiClient from '../../api/client';

// Format currency from cents
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'Ongoing';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short'
  });
};

// Format percentage with sign
const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const parseAuditValue = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatAuditPeriod = (value) => {
  if (!value || typeof value !== 'object') return null;
  const start = formatDate(value.effective_start_date);
  const end = value.effective_end_date ? formatDate(value.effective_end_date) : 'Ongoing';
  if (!value.effective_start_date && !value.effective_end_date) return null;
  return `${start} - ${end}`;
};

const formatAuditPrice = (value) => {
  if (!value || typeof value !== 'object' || value.price_cents === undefined || value.price_cents === null) {
    return null;
  }
  return formatCurrency(Number(value.price_cents));
};

const getAuditActionLabel = (entry) => {
  const changeType = (entry.change_type || '').toLowerCase();
  if (changeType.includes('delete')) return 'Price Tier Removed';
  if (changeType.includes('update') || changeType.includes('manual')) return 'Price Tier Updated';
  if (changeType.includes('create') || changeType.includes('system')) return 'Price Tier Added';
  return 'Price Change';
};

const getAuditDisplay = (entry) => {
  const oldValue = parseAuditValue(entry.old_value);
  const newValue = parseAuditValue(entry.new_value);
  const current = newValue || oldValue;

  if (current && typeof current === 'object') {
    const tierName = current.tier_name || 'Unnamed tier';
    const price = formatAuditPrice(current);
    const period = formatAuditPeriod(current);
    const summaryParts = [
      tierName,
      price ? `set to ${price}` : null,
      period ? `for ${period}` : null
    ].filter(Boolean);

    return {
      title: summaryParts.join(' '),
      details: [
        current.effective_start_date ? `Start ${formatDate(current.effective_start_date)}` : null,
        current.effective_end_date ? `End ${formatDate(current.effective_end_date)}` : 'End Ongoing',
        price ? `Price ${price}` : null
      ].filter(Boolean)
    };
  }

  if (current) {
    return {
      title: String(current),
      details: entry.field_name ? [`Field ${entry.field_name}`] : []
    };
  }

  return {
    title: 'Price schedule changed',
    details: entry.field_name ? [`Field ${entry.field_name}`] : []
  };
};

// Get status badge styling
const getCoverageStatusStyle = (status) => {
  switch (status) {
    case 'complete':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'gaps_detected':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'no_schedule':
    default:
      return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

// Price Tier Row Component
function PriceTierRow({ tier, onEdit, onDelete, customerTargetCents }) {
  const vsTargetPercent = tier.vs_target_percent;
  const isAboveTarget = vsTargetPercent && vsTargetPercent > 0;
  const isBelowTarget = vsTargetPercent && vsTargetPercent < 0;

  return (
    <tr className="border-b last:border-b-0 hover:bg-gray-50 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">
            {formatDate(tier.effective_start_date)}
          </span>
          <ArrowRight className="h-3 w-3 text-gray-400" />
          <span className={`text-sm ${tier.is_open_ended ? 'text-blue-600 font-medium' : 'text-gray-900'}`}>
            {tier.is_open_ended ? 'Ongoing' : formatDate(tier.effective_end_date)}
          </span>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-sm font-semibold text-gray-900">
          {formatCurrency(tier.price_cents)}
        </span>
      </td>
      <td className="py-3 px-4">
        {customerTargetCents && vsTargetPercent !== null ? (
          <div className={`inline-flex items-center gap-1 text-sm font-medium ${
            isAboveTarget ? 'text-green-600' : isBelowTarget ? 'text-red-600' : 'text-gray-500'
          }`}>
            {isAboveTarget ? <TrendingUp className="h-3 w-3" /> : isBelowTarget ? <TrendingDown className="h-3 w-3" /> : null}
            {formatPercent(vsTargetPercent)}
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )}
      </td>
      <td className="py-3 px-4">
        <span className="text-sm text-gray-600">
          {tier.tier_name || '-'}
        </span>
      </td>
      <td className="py-3 px-4 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(tier)}
            className="h-8 w-8 p-0 hover:bg-gray-100"
          >
            <Pencil className="h-4 w-4 text-gray-500" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(tier)}
            className="h-8 w-8 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Audit History Item Component
function AuditHistoryItem({ entry }) {
  const display = getAuditDisplay(entry);
  return (
    <div className="py-3 px-4 border-b last:border-b-0 hover:bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-blue-100 bg-blue-50 text-blue-700">
              {getAuditActionLabel(entry)}
            </Badge>
            <span className="text-sm font-medium text-gray-900">
              {display.title}
            </span>
          </div>
          {display.details.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
              {display.details.map((detail) => (
                <span key={detail}>{detail}</span>
              ))}
            </div>
          )}
          {entry.change_reason && (
            <div className="text-xs text-gray-500 mt-1">
              Reason: {entry.change_reason}
            </div>
          )}
        </div>
        <div className="text-xs text-gray-400 ml-4 whitespace-nowrap">
          {new Date(entry.changed_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
}

// Main PricePlanningSection Component
export default function PricePlanningSection({ partId, part, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [pricingData, setPricingData] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    effective_start_date: '',
    effective_end_date: '',
    price_dollars: '',
    tier_name: '',
    is_open_ended: false
  });
  
  const [changeReason, setChangeReason] = useState('');

  // Fetch comprehensive pricing data
  const fetchPricingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/parts/${partId}/pricing-summary`);
      if (response.data.success) {
        setPricingData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching pricing data:', err);
      setError('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    if (partId) {
      fetchPricingData();
    }
  }, [partId, fetchPricingData]);

  // Reset form
  const resetForm = () => {
    setFormData({
      effective_start_date: '',
      effective_end_date: '',
      price_dollars: '',
      tier_name: '',
      is_open_ended: false
    });
    setChangeReason('');
    setError(null);
  };

  // Check if reason is required (only for DELETE operations now)
  const requiresReason = pricingData?.edits_require_audit;

  // Handle add tier click - goes directly to dialog (reason not required for add)
  const handleAddTierClick = () => {
    resetForm();
    setShowAddDialog(true);
  };

  // Handle add tier submission
  const handleAddTier = async () => {
    if (!formData.effective_start_date || !formData.price_dollars) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (changeReason) {
        params.append('change_reason', changeReason);
      }

      const payload = {
        effective_start_date: formData.effective_start_date,
        effective_end_date: formData.is_open_ended ? null : formData.effective_end_date || null,
        price_cents: Math.round(parseFloat(formData.price_dollars) * 100),
        tier_name: formData.tier_name || null
      };

      const url = `/api/v1/parts/${partId}/price-schedule${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiClient.post(url, payload);
      
      if (response.data.success) {
        setShowAddDialog(false);
        resetForm();
        fetchPricingData();
        onUpdate?.();
      } else {
        setError(response.data.detail || 'Failed to add tier');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add tier');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit tier - goes directly to dialog (reason not required for edit)
  const handleEditTierClick = (tier) => {
    setSelectedTier(tier);
    setFormData({
      effective_start_date: tier.effective_start_date || '',
      effective_end_date: tier.effective_end_date || '',
      price_dollars: tier.price_dollars?.toString() || '',
      tier_name: tier.tier_name || '',
      is_open_ended: tier.is_open_ended
    });
    setShowEditDialog(true);
  };

  // Handle edit tier submission
  const handleEditTier = async () => {
    if (!selectedTier) return;

    setSubmitting(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (changeReason) {
        params.append('change_reason', changeReason);
      }

      const payload = {
        effective_start_date: formData.effective_start_date,
        effective_end_date: formData.is_open_ended ? null : formData.effective_end_date || null,
        price_cents: Math.round(parseFloat(formData.price_dollars) * 100),
        tier_name: formData.tier_name || null,
        clear_end_date: formData.is_open_ended
      };

      const url = `/api/v1/parts/${partId}/price-schedule/${selectedTier.id}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiClient.put(url, payload);
      
      if (response.data.success) {
        setShowEditDialog(false);
        setSelectedTier(null);
        resetForm();
        fetchPricingData();
        onUpdate?.();
      } else {
        setError(response.data.detail || 'Failed to update tier');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update tier');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete tier (with reason check)
  const handleDeleteTierClick = (tier) => {
    setSelectedTier(tier);
    if (requiresReason) {
      setPendingAction('delete');
      setShowReasonDialog(true);
    } else {
      setShowDeleteDialog(true);
    }
  };

  // Handle delete tier submission
  const handleDeleteTier = async () => {
    if (!selectedTier) return;

    setSubmitting(true);

    try {
      const params = new URLSearchParams();
      if (changeReason) {
        params.append('change_reason', changeReason);
      }

      const url = `/api/v1/parts/${partId}/price-schedule/${selectedTier.id}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiClient.delete(url);
      
      if (response.data.success) {
        setShowDeleteDialog(false);
        setSelectedTier(null);
        resetForm();
        fetchPricingData();
        onUpdate?.();
      }
    } catch (err) {
      console.error('Error deleting tier:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reason dialog confirmation (only for DELETE operations)
  const handleReasonConfirm = () => {
    if (!changeReason || changeReason.length < 10) {
      setError('Please provide a reason (minimum 10 characters)');
      return;
    }
    
    setShowReasonDialog(false);
    
    // Only delete uses reason dialog now
    if (pendingAction === 'delete') {
      setShowDeleteDialog(true);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Loading pricing data...
        </CardContent>
      </Card>
    );
  }

  const tiers = pricingData?.schedule_tiers || [];
  const coverage = pricingData?.coverage;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Contract Price Schedule
              <InfoHint title="Contract Price Schedule">
                Official date-effective quote or award terms. Use the S&OP grid above for LTA,
                volume, price adjustment, and scenario modeling.
              </InfoHint>
            </CardTitle>
            <CardDescription className="mt-1">
              Official price terms and audit trail across the contract lifecycle
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Pricing Stage Badge */}
            <Badge variant="outline" className="text-xs font-medium">
              {pricingData?.pricing_stage === 'locked' && <Shield className="h-3 w-3 mr-1" />}
              {pricingData?.pricing_stage?.charAt(0).toUpperCase() + pricingData?.pricing_stage?.slice(1) || 'Planning'}
            </Badge>
            <Button 
              size="sm"
              onClick={handleAddTierClick}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Tier
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Reference Section - Customer Target Price */}
        {pricingData?.customer_target_cents && (
          <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Tag className="h-4 w-4 text-blue-500" />
                <span className="font-medium">Customer Target Price</span>
                <InfoHint title="Customer Target Price">
                  Reference target for this part. Compare your quoted price against it; it does not drive revenue.
                </InfoHint>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {formatCurrency(pricingData.customer_target_cents)}
              </span>
            </div>
          </div>
        )}

        {/* Coverage Status Alert */}
        {coverage && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${getCoverageStatusStyle(coverage.status)}`}>
            {coverage.status === 'complete' ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                <span>Coverage: Complete through {coverage.eop_date}</span>
              </>
            ) : coverage.status === 'gaps_detected' ? (
              <>
                <AlertTriangle className="h-4 w-4" />
                <span>Coverage: Gaps detected in pricing schedule</span>
              </>
            ) : (
              <>
                <Info className="h-4 w-4" />
                <span>No pricing schedule defined</span>
              </>
            )}
          </div>
        )}

        {/* Audit Warning for Post-Award */}
        {pricingData?.edits_require_audit && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <Shield className="h-4 w-4" />
            <span>Changes will be logged (post-award status)</span>
          </div>
        )}

        {/* Price Tiers Table */}
        {tiers.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Price/Unit</th>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">vs Target</th>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tier Name</th>
                  <th className="text-right py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => (
                  <PriceTierRow
                    key={tier.id}
                    tier={tier}
                    onEdit={handleEditTierClick}
                    onDelete={handleDeleteTierClick}
                    customerTargetCents={pricingData?.customer_target_cents}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No pricing tiers defined</p>
            <p className="text-xs text-gray-400 mt-1">Click &quot;Add Tier&quot; to create your pricing schedule</p>
          </div>
        )}

        {/* Summary Section */}
        {tiers.length > 0 && (
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-1.5 text-sm font-medium text-blue-900">
              <Tag className="h-4 w-4" />
              Weighted Average Price
              <InfoHint title="Weighted Average Price">
                Average contract price per unit across the schedule, weighted by each tier&apos;s duration.
                S&OP remains the modeling surface for scenario price changes.
              </InfoHint>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-blue-900">
                {formatCurrency(pricingData?.weighted_avg_price_cents)}
              </div>
              {pricingData?.total_contract_value_formatted && (
                <div className="text-xs text-blue-600 mt-1">
                  Contract Value: {pricingData.total_contract_value_formatted}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tier Summary */}
        {tiers.length > 0 && (
          <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t">
            <span>{tiers.length} tier(s)</span>
            <span className="text-gray-300">|</span>
            <span>From {formatDate(tiers[0]?.effective_start_date)}</span>
            {coverage?.contract_years && (
              <>
                <span className="text-gray-300">|</span>
                <span>{coverage.contract_years} year contract</span>
              </>
            )}
          </div>
        )}

        {/* Audit History (Collapsible) */}
        {pricingData?.has_audit_history && (
          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Clock className="h-4 w-4" />
                  Change History ({pricingData.recent_changes?.length || 0})
                </div>
                {historyOpen ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 border rounded-lg overflow-hidden">
                {pricingData.recent_changes?.map((entry) => (
                  <AuditHistoryItem key={entry.id} entry={entry} />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>

      {/* Change Reason Dialog (for post-award edits) */}
      <Dialog open={showReasonDialog} onOpenChange={setShowReasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-600" />
              Change Reason Required
            </DialogTitle>
            <DialogDescription>
              This part is in post-award status. Please provide a reason for this change.
              This will be recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            <Textarea
              placeholder="Explain the reason for this change (e.g., contract renegotiation, material cost adjustment)..."
              value={changeReason}
              onChange={(e) => {
                setChangeReason(e.target.value);
                setError(null);
              }}
              rows={3}
            />
            <div className="text-xs text-gray-500 mt-1">
              {changeReason.length}/10 characters minimum
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowReasonDialog(false);
              setPendingAction(null);
              setChangeReason('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleReasonConfirm}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tier Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Price Tier</DialogTitle>
            <DialogDescription>
              Create a new pricing tier for this contract period.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Start Date *</label>
                <Input
                  type="date"
                  value={formData.effective_start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_start_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">End Date</label>
                <Input
                  type="date"
                  value={formData.effective_end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_end_date: e.target.value }))}
                  disabled={formData.is_open_ended}
                  className="mt-1"
                />
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={formData.is_open_ended}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      is_open_ended: e.target.checked,
                      effective_end_date: e.target.checked ? '' : prev.effective_end_date
                    }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-500">Open-ended (no end date)</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Price per Unit ($) *</label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.price_dollars}
                onChange={(e) => setFormData(prev => ({ ...prev, price_dollars: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Tier Name</label>
              <Input
                type="text"
                placeholder="e.g., Initial, LTA Year 3, Final"
                value={formData.tier_name}
                onChange={(e) => setFormData(prev => ({ ...prev, tier_name: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTier} disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Tier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tier Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Price Tier</DialogTitle>
            <DialogDescription>
              Update the pricing tier details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Start Date *</label>
                <Input
                  type="date"
                  value={formData.effective_start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_start_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">End Date</label>
                <Input
                  type="date"
                  value={formData.effective_end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_end_date: e.target.value }))}
                  disabled={formData.is_open_ended}
                  className="mt-1"
                />
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={formData.is_open_ended}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      is_open_ended: e.target.checked,
                      effective_end_date: e.target.checked ? '' : prev.effective_end_date
                    }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-500">Open-ended (no end date)</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Price per Unit ($) *</label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.price_dollars}
                onChange={(e) => setFormData(prev => ({ ...prev, price_dollars: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Tier Name</label>
              <Input
                type="text"
                placeholder="e.g., Initial, LTA Year 3, Final"
                value={formData.tier_name}
                onChange={(e) => setFormData(prev => ({ ...prev, tier_name: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTier} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Price Tier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this price tier? This action cannot be undone.
              {selectedTier && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                  <strong>{formatDate(selectedTier.effective_start_date)}</strong>
                  {' → '}
                  <strong>{selectedTier.is_open_ended ? 'Ongoing' : formatDate(selectedTier.effective_end_date)}</strong>
                  {' @ '}
                  <strong>{formatCurrency(selectedTier.price_cents)}</strong>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTier}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
